import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Send, Loader2, Mic, Square, Plus, MessageSquareQuote,
  Globe, Sparkles, Plug, Image as ImageIcon, FileText, X, Wand2, GripHorizontal, Volume2
} from 'lucide-react';
import { ModelSelector } from './ModelSelector';
import { TokenUsagePanel } from './TokenUsagePanel';
import { VoiceUsagePanel } from './VoiceUsagePanel';

export type ChatStrategy = 'plan' | 'ask' | 'craft';
export type TeamMode = 'solo' | 'team' | 'orchestrate';

interface ChatInputToolbarProps {
  // Message handling
  onSend: (text: string) => void;
  isTyping: boolean;
  isListening: boolean;
  onToggleListening: () => void;
  onAbort: () => void;
  disabled?: boolean;

  // Attachments
  pendingAttachments: any[];
  onRemoveAttachment: (id: string) => void;
  onOpenFilePicker: () => void;

  // Skills & connectors
  installedSkills: string[];
  mcpConnectors: string[];

  // Draft
  draftText: string;
  onDraftChange: (text: string) => void;

  // Mode
  strategy: ChatStrategy;
  onStrategyChange: (s: ChatStrategy) => void;
  // Multi-agent team mode
  teamMode?: TeamMode;
  onTeamModeChange?: (m: TeamMode) => void;

  // i18n
  t?: any;
  ui: (zh: string, en: string) => string;

  // Refs
  messageInputRef: React.RefObject<HTMLTextAreaElement | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;

  // Conversation context
  conversationId?: string;
  isNewConversation?: boolean;

  // Real interactions (previously dead buttons)
  quoteMessages?: any[];
  onQuote?: (text: string) => void;
  onSkillSelect?: (skill: string) => void;
  onConnectorSelect?: (connector: string) => void;
  onOpenImagePicker?: () => void;
  // Ctrl+V paste image
  onImagePaste?: (file: File) => void;
  // Prompt enhancement
  onEnhancePrompt?: (text: string) => Promise<string>;
  // Slash commands
  slashCommands?: { id: string; label: string; desc: string; icon: string; action: string; text?: string }[];
  onSlashCommand?: (cmd: any) => void;
}

const TEAM_OPTIONS: { id: TeamMode; label: string; desc: string; icon: string }[] = [
  { id: 'solo', label: '单人', desc: '单Agent独立执行', icon: 'User' },
  { id: 'team', label: 'Team', desc: '多Agent并行协作', icon: 'Users' },
  { id: 'orchestrate', label: '编排', desc: '自动分解→分派→汇总', icon: 'Network' },
];

const STRATEGY_OPTIONS: { id: ChatStrategy; label: string; desc: string }[] = [
  { id: 'plan', label: '规划', desc: '分析 → 计划 → 确认 → 执行' },
  { id: 'ask', label: '提问', desc: '只问答解释，不改文件' },
  { id: 'craft', label: '创作', desc: '分析 → 直接写代码执行' },
];

export function ChatInputToolbar({
  onSend,
  isTyping,
  isListening,
  onToggleListening,
  onAbort,
  disabled = false,
  pendingAttachments,
  onRemoveAttachment,
  onOpenFilePicker,
  installedSkills,
  mcpConnectors,
  draftText,
  onDraftChange,
  strategy,
  onStrategyChange,
  teamMode = 'solo',
  onTeamModeChange,
  t,
  ui,
  messageInputRef,
  fileInputRef,
  conversationId,
  isNewConversation = false,
  quoteMessages = [],
  onQuote,
  onSkillSelect,
  onConnectorSelect,
  onOpenImagePicker,
  onImagePaste,
  onEnhancePrompt,
  slashCommands = [],
  onSlashCommand,
}: ChatInputToolbarProps) {
  const [showFileMenu, setShowFileMenu] = useState(false);
  const [showSkillMenu, setShowSkillMenu] = useState(false);
  const [showConnectorMenu, setShowConnectorMenu] = useState(false);
  const [showQuoteMenu, setShowQuoteMenu] = useState(false);
  const [tokenPct, setTokenPct] = useState<number>(0);
  const [enhancing, setEnhancing] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashIdx, setSlashIdx] = useState(0);
  const slashMenuRef = useRef<HTMLDivElement>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [showMentionMenu, setShowMentionMenu] = useState(false);
  const [mentionIndex, setMentionIndex] = useState(0);
  const mentionMenuRef = useRef<HTMLDivElement>(null);

  // ====== v3.6.63: 可拖拽调整输入框高度 ======
  const MIN_INPUT_HEIGHT = 120; // v3.6.66: 默认 4 行高度
  const MAX_INPUT_HEIGHT = 320;
  const [inputHeight, setInputHeight] = useState<number>(() => {
    try {
      const saved = localStorage.getItem('xf_chat_input_height');
      if (saved) {
        const n = parseInt(saved, 10);
        if (n >= MIN_INPUT_HEIGHT && n <= MAX_INPUT_HEIGHT) return n;
      }
    } catch {}
    return MIN_INPUT_HEIGHT;
  });
  useEffect(() => {
    try { localStorage.setItem('xf_chat_input_height', String(inputHeight)); } catch {}
  }, [inputHeight]);
  const dragStateRef = useRef<{ startY: number; startH: number } | null>(null);
  // v3.6.70: voice usage panel
  const [voiceUsageOpen, setVoiceUsageOpen] = useState(false);
  const voiceBtnRef = useRef<HTMLButtonElement>(null);
  const [voiceBtnRect, setVoiceBtnRect] = useState<{ top: number; right: number } | null>(null);
  const onResizeHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragStateRef.current = { startY: e.clientY, startH: inputHeight };
    const onMove = (ev: MouseEvent) => {
      if (!dragStateRef.current) return;
      const delta = dragStateRef.current.startY - ev.clientY;
      const newH = Math.max(MIN_INPUT_HEIGHT, Math.min(MAX_INPUT_HEIGHT, dragStateRef.current.startH + delta));
      setInputHeight(newH);
    };
    const onUp = () => {
      dragStateRef.current = null;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
  };
  const onResizeHandleDoubleClick = () => setInputHeight(MIN_INPUT_HEIGHT);
  // ====== v3.6.63 drag end ======

  const fileMenuRef = useRef<HTMLDivElement>(null);
  const skillMenuRef = useRef<HTMLDivElement>(null);
  const connectorMenuRef = useRef<HTMLDivElement>(null);
  const quoteMenuRef = useRef<HTMLDivElement>(null);

  // Fetch token percentage periodically for context warning
  // Now uses conversationId to get per-conversation stats instead of 24h global
  useEffect(() => {
    const fetchPct = async () => {
      try {
        // New conversation with no messages → no need to fetch, hide warning
        if (isNewConversation) {
          setTokenPct(0);
          return;
        }
        const url = conversationId
          ? `/api/token-breakdown?conversationId=${encodeURIComponent(conversationId)}`
          : '/api/token-breakdown';
        const res = await fetch(url, { credentials: 'include' });
        const json = await res.json();
        if (json && typeof json.percentage === 'number') {
          setTokenPct(json.percentage);
        }
      } catch {}
    };
    fetchPct();
    const interval = setInterval(fetchPct, 30000);
    return () => clearInterval(interval);
  }, [conversationId, isNewConversation]);

  // Close menus on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (fileMenuRef.current && !fileMenuRef.current.contains(e.target as Node)) setShowFileMenu(false);
      if (skillMenuRef.current && !skillMenuRef.current.contains(e.target as Node)) setShowSkillMenu(false);
      if (connectorMenuRef.current && !connectorMenuRef.current.contains(e.target as Node)) setShowConnectorMenu(false);
      if (quoteMenuRef.current && !quoteMenuRef.current.contains(e.target as Node)) setShowQuoteMenu(false);
      if (mentionMenuRef.current && !mentionMenuRef.current.contains(e.target as Node)) setShowMentionMenu(false);
      if (slashMenuRef.current && !slashMenuRef.current.contains(e.target as Node)) setShowSlashMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const hasText = draftText.trim().length > 0;
  const canSend = (hasText || pendingAttachments.length > 0) && !isTyping && !disabled;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSend) return;
    onSend(draftText);
  };

  // @mention filtering
  const mentionItems = [
    ...installedSkills.map(s => ({ type: 'skill', name: s })),
    ...mcpConnectors.map(c => ({ type: 'connector', name: c })),
  ];

  const filteredMentions = mentionQuery
    ? mentionItems.filter(m => m.name.toLowerCase().includes(mentionQuery.toLowerCase()))
    : mentionItems;

  const handleMentionSelect = (item: { type: string; name: string }) => {
    const prefix = item.type === 'skill' ? '@skill ' : '@connector ';
    const current = draftText || '';
    // Replace the last @mention pattern with the selected item
    const mentionPattern = /@(skill|connector)?\s?\w*$/;
    const newText = current.replace(mentionPattern, '');
    onDraftChange(newText + prefix + item.name + ' ');
    setShowMentionMenu(false);
    setMentionQuery('');
    if (messageInputRef.current) {
      setTimeout(() => messageInputRef.current?.focus(), 50);
    }
  };

  // Detect @mention in draft text
  useEffect(() => {
    const text = draftText || '';
    const match = text.match(/@(skill|connector)?\s?(\w*)$/);
    if (match && mentionItems.length > 0) {
      setShowMentionMenu(true);
      setMentionQuery(match[2] || '');
      setMentionIndex(0);
    } else {
      setShowMentionMenu(false);
    }
  }, [draftText, installedSkills, mcpConnectors]);

  // Slash command detection
  const filteredSlashCommands = slashQuery
    ? slashCommands.filter(c => c.label.toLowerCase().includes(slashQuery.toLowerCase()) || c.id.toLowerCase().includes(slashQuery.toLowerCase()))
    : slashCommands;

  const handleSlashSelect = (cmd: { id: string; action: string; text?: string; label: string }) => {
    setShowSlashMenu(false);
    setSlashQuery('');
    if (cmd.action === 'clear' || cmd.action === 'export') {
      onSlashCommand?.(cmd);
      onDraftChange('');
    } else if (cmd.text) {
      onDraftChange(cmd.text);
      if (messageInputRef.current) messageInputRef.current.focus();
    }
  };

  // Paste image handler
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const blob = items[i].getAsFile();
        if (blob && onImagePaste) {
          onImagePaste(blob);
          e.preventDefault();
          return;
        }
      }
    }
  };

  const handleEnhance = async () => {
    var text = draftText?.trim();
    if (!text || !onEnhancePrompt) return;
    setEnhancing(true);
    try {
      var result = await onEnhancePrompt(text);
      if (result) onDraftChange(result);
    } catch {} finally {
      setEnhancing(false);
    }
  };

  const recentQuoteMessages = (quoteMessages || [])
    .filter((m: any) => m && m.type !== 'tool' && m.text)
    .slice(-12)
    .reverse();

  return (
    <div className="space-y-3">
      {/* Pending Attachments */}
      {pendingAttachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pendingAttachments.map(item => (
            <div key={item.id} className="flex max-w-full items-center gap-2 rounded-2xl border border-white/10 bg-black/30 px-3 py-2 text-xs text-white/70">
              {item.kind === 'image' && item.downloadUrl ? (
                <img src={item.downloadUrl} alt={item.fileName} className="h-8 w-8 rounded-lg object-cover" />
              ) : item.kind === 'image' ? (
                <ImageIcon size={16} className="shrink-0 text-celestial-saturn" />
              ) : (
                <FileText size={16} className="shrink-0 text-white/45" />
              )}
              <span className="max-w-[220px] truncate">{item.fileName}</span>
              <button
                type="button"
                onClick={() => onRemoveAttachment(item.id)}
                className="ml-1 rounded-full p-0.5 text-white/30 transition-colors hover:bg-white/10 hover:text-white/70"
                title={ui('移除附件', 'Remove attachment')}
                aria-label={ui('移除附件', 'Remove attachment')}
              >
                <X size={13} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar Row */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* + Add File Button */}
        <div className="relative" ref={fileMenuRef}>
          <button
            type="button"
            onClick={() => { setShowFileMenu(!showFileMenu); setShowQuoteMenu(false); setShowSkillMenu(false); setShowConnectorMenu(false); }}
            disabled={isTyping || disabled}
            className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-xs font-bold text-white/45 transition-all hover:border-white/20 hover:bg-white/[0.08] hover:text-white/70 disabled:opacity-40"
            title={ui('添加文件', 'Add file')}
          >
            <Plus size={14} />
          </button>
          <AnimatePresence>
            {showFileMenu && (
              <motion.div
                initial={{ opacity: 0, y: 4, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.96 }}
                className="absolute bottom-full mb-2 left-0 z-50 min-w-[160px] rounded-xl border border-white/10 bg-zinc-900/95 p-1 shadow-2xl backdrop-blur-xl"
              >
                <button onClick={() => { (onOpenImagePicker || onOpenFilePicker)(); setShowFileMenu(false); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-white/65 hover:bg-white/5 hover:text-white transition-colors">
                  <ImageIcon size={14} className="text-celestial-saturn" /> {ui('添加图片', 'Add Image')}
                </button>
                <button onClick={() => { onOpenFilePicker(); setShowFileMenu(false); }} className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-white/65 hover:bg-white/5 hover:text-white transition-colors">
                  <FileText size={14} className="text-white/40" /> {ui('添加文件', 'Add File')}
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Quote Conversation */}
        <div className="relative" ref={quoteMenuRef}>
          <button
            type="button"
            onClick={() => { setShowQuoteMenu(!showQuoteMenu); setShowFileMenu(false); setShowSkillMenu(false); setShowConnectorMenu(false); }}
            disabled={isTyping || disabled}
            className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-xs font-bold text-white/45 transition-all hover:border-white/20 hover:bg-white/[0.08] hover:text-white/70 disabled:opacity-40"
            title={ui('引用对话', 'Quote conversation')}
          >
            <MessageSquareQuote size={14} />
          </button>
          <AnimatePresence>
            {showQuoteMenu && (
              <motion.div
                initial={{ opacity: 0, y: 4, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.96 }}
                className="absolute bottom-full mb-2 left-0 z-50 min-w-[180px] rounded-xl border border-white/10 bg-zinc-900/95 p-2 shadow-2xl backdrop-blur-xl"
              >
                <p className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white/30">
                  {ui('引用最近的对话', 'Quote recent messages')}
                </p>
                {recentQuoteMessages.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-white/35 text-center">{ui('暂无可引用消息', 'No messages to quote')}</p>
                ) : (
                  <div className="max-h-56 overflow-y-auto custom-scrollbar">
                    {recentQuoteMessages.map((m: any, idx: number) => (
                      <button
                        key={m.id || idx}
                        type="button"
                        onClick={() => { onQuote?.(m.text); setShowQuoteMenu(false); }}
                        className="flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-xs text-white/55 hover:bg-white/5 hover:text-white/80 transition-colors"
                      >
                        <MessageSquareQuote size={14} className="mt-0.5 shrink-0 text-celestial-saturn" />
                        <span className="line-clamp-2 max-w-[200px] truncate">{m.text}</span>
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Team Mode: Solo | Team | Orchestrate */}
        <div className="flex rounded-lg border border-celestial-nebula/20 bg-celestial-nebula/5 p-0.5">
          {TEAM_OPTIONS.map(function(opt) {
            var active = teamMode === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={function() { onTeamModeChange?.(opt.id); }}
                className={'rounded-md px-2 py-1 text-[11px] font-bold transition-all ' + (active ? 'bg-celestial-nebula/20 text-celestial-nebula shadow-sm' : 'text-white/35 hover:text-white/60')}
                title={opt.desc}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Separator */}
        <div className="mx-1 h-4 w-px bg-white/10" />

        {/* Strategy Switcher: Plan | Ask | Craft */}
        <div className="flex rounded-lg border border-white/10 bg-white/[0.04] p-0.5">
          {STRATEGY_OPTIONS.map((opt) => {
            const active = strategy === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => onStrategyChange(opt.id)}
                className={`rounded-md px-2.5 py-1 text-[11px] font-bold transition-all ${
                  active
                    ? 'bg-white/15 text-white shadow-sm'
                    : 'text-white/40 hover:text-white/65'
                }`}
                title={opt.desc}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Skills Dropdown */}
        <div className="relative" ref={skillMenuRef}>
          <button
            type="button"
            onClick={() => { setShowSkillMenu(!showSkillMenu); setShowFileMenu(false); setShowQuoteMenu(false); setShowConnectorMenu(false); }}
            disabled={isTyping || disabled}
            className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-xs font-bold text-white/45 transition-all hover:border-white/20 hover:bg-white/[0.08] hover:text-white/70 disabled:opacity-40"
            title={ui('技能', 'Skills')}
          >
            <Sparkles size={14} />
            <span className="hidden sm:inline">{ui('技能', 'Skills')}</span>
            {installedSkills.length > 0 && (
              <span className="ml-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-celestial-saturn/20 px-1 text-[10px] font-black text-celestial-saturn">
                {installedSkills.length}
              </span>
            )}
          </button>
          <AnimatePresence>
            {showSkillMenu && (
              <motion.div
                initial={{ opacity: 0, y: 4, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.96 }}
                className="absolute bottom-full mb-2 left-0 z-50 min-w-[180px] rounded-xl border border-white/10 bg-zinc-900/95 p-1 shadow-2xl backdrop-blur-xl"
              >
                {installedSkills.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-white/35 text-center">{ui('无已安装技能', 'No skills installed')}</p>
                ) : (
                  installedSkills.map((skill) => (
                    <button
                      key={skill}
                      type="button"
                      onClick={() => { onSkillSelect?.(skill); setShowSkillMenu(false); }}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-white/55 hover:bg-white/5 hover:text-white/80 transition-colors"
                    >
                      <Sparkles size={14} className="text-celestial-saturn" />
                      <span className="capitalize">{skill}</span>
                    </button>
                  ))
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Connectors Dropdown */}
        <div className="relative" ref={connectorMenuRef}>
          <button
            type="button"
            onClick={() => { setShowConnectorMenu(!showConnectorMenu); setShowFileMenu(false); setShowQuoteMenu(false); setShowSkillMenu(false); }}
            disabled={isTyping || disabled}
            className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1.5 text-xs font-bold text-white/45 transition-all hover:border-white/20 hover:bg-white/[0.08] hover:text-white/70 disabled:opacity-40"
            title={ui('连接器及MCP', 'Connectors & MCP')}
          >
            <Plug size={14} />
            <span className="hidden sm:inline">{ui('连接器', 'Connectors')}</span>
            {mcpConnectors.length > 0 && (
              <span className="ml-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-emerald-400/20 px-1 text-[10px] font-black text-emerald-400">
                {mcpConnectors.length}
              </span>
            )}
          </button>
          <AnimatePresence>
            {showConnectorMenu && (
              <motion.div
                initial={{ opacity: 0, y: 4, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 4, scale: 0.96 }}
                className="absolute bottom-full mb-2 left-0 z-50 min-w-[180px] rounded-xl border border-white/10 bg-zinc-900/95 p-1 shadow-2xl backdrop-blur-xl"
              >
                {mcpConnectors.length === 0 ? (
                  <p className="px-3 py-4 text-xs text-white/35 text-center">{ui('无已连接MCP', 'No MCP connected')}</p>
                ) : (
                  mcpConnectors.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => { onConnectorSelect?.(c); setShowConnectorMenu(false); }}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-white/55 hover:bg-white/5 hover:text-white/80 transition-colors"
                    >
                      <Globe size={14} className="text-emerald-400" />
                      <span className="capitalize">{c}</span>
                    </button>
                  ))
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right side: Mic + Enhance + Model Selector + Send */}
        <div className="flex items-center gap-1.5">
          {/* Mic Button */}
          <button
            type="button"
            onClick={onToggleListening}
            disabled={isTyping || disabled}
            className={`flex h-8 w-8 items-center justify-center rounded-lg border transition-all ${
              isListening
                ? 'border-celestial-mars/40 bg-celestial-mars/20 text-celestial-mars animate-pulse'
                : 'border-white/10 bg-white/[0.04] text-white/40 hover:border-white/20 hover:text-white'
            } disabled:opacity-30`}
          >
            <Mic size={16} />
          </button>

          {/* Prompt Enhance Button */}
          {draftText && draftText.trim().length > 0 && (
            <button
              type="button"
              onClick={handleEnhance}
              disabled={isTyping || disabled || enhancing || !draftText.trim()}
              className={"flex h-8 w-8 items-center justify-center rounded-lg border transition-all " + (enhancing ? 'border-amber-400/40 bg-amber-400/20 text-amber-400 animate-pulse' : 'border-white/10 bg-white/[0.04] text-white/40 hover:border-amber-400/30 hover:text-amber-300')}
              title="增强提示词 - AI 帮你优化扩写"
            >
              {enhancing ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
            </button>
          )}

          {/* Model Selector */}
          <ModelSelector compact />

          {/* Send / Abort Button */}
          {isTyping ? (
            <button
              type="button"
              onClick={onAbort}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-red-500/30 bg-red-500/15 text-red-400 transition-all hover:bg-red-500/25"
            >
              <Square size={14} />
            </button>
          ) : (
            <button
              type="submit"
              form="chat-input-form"
              disabled={!canSend}
              className="flex h-8 w-8 items-center justify-center rounded-lg bg-celestial-saturn text-black transition-all hover:scale-105 disabled:opacity-30 disabled:hover:scale-100"
            >
              <Send size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Secondary info row: token + voice usage (moved out of main toolbar to declutter) */}
      <div className="mt-1.5 flex items-center justify-end gap-1.5">
        <TokenUsagePanel compact />
        <div className="relative">
          <button
            ref={voiceBtnRef}
            type="button"
            onClick={() => {
              if (!voiceUsageOpen) {
                if (voiceBtnRef.current) {
                  const rect = voiceBtnRef.current.getBoundingClientRect();
                  setVoiceBtnRect({ top: rect.top, right: rect.right });
                }
                setVoiceUsageOpen(true);
              } else { setVoiceUsageOpen(false); }
            }}
            className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/[0.04] px-2 py-1 text-xs font-bold text-white/45 transition-all hover:border-white/20 hover:bg-white/[0.08] hover:text-white/70"
          >
            <Volume2 size={14} />
            <span className="hidden sm:inline">语音用量</span>
          </button>
        </div>
        <VoiceUsagePanel
          isOpen={voiceUsageOpen}
          onClose={() => setVoiceUsageOpen(false)}
          buttonRect={voiceBtnRect}
        />
      </div>

      {/* Context overflow warning bar (above input) */}
      <AnimatePresence>
        {tokenPct >= 70 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className={`mb-2 flex items-center gap-2 rounded-xl border px-3 py-1.5 text-xs font-bold ${
              tokenPct >= 90
                ? 'border-celestial-mars/30 bg-celestial-mars/10 text-celestial-mars'
                : 'border-amber-400/30 bg-amber-400/10 text-amber-400'
            }`}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            <span>
              {tokenPct >= 90
                ? `上下文 ${tokenPct.toFixed(0)}% — 接近满载，建议开启新会话`
                : `上下文 ${tokenPct.toFixed(0)}% — 用量较高`}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Text Input Row */}
      {/* / Slash Command Menu */}
      <AnimatePresence>
        {showSlashMenu && slashCommands.length > 0 && (
          <motion.div
            ref={slashMenuRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="relative z-50 mb-1"
          >
            <div className="absolute bottom-full left-0 w-64 rounded-xl border border-white/10 bg-zinc-900/95 p-1 shadow-2xl backdrop-blur-xl max-h-60 overflow-y-auto custom-scrollbar">
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white/30">命令</div>
              {(slashQuery ? filteredSlashCommands : slashCommands).slice(0, 8).map((cmd, i) => (
                <button
                  key={cmd.id}
                  type="button"
                  onClick={() => handleSlashSelect(cmd)}
                  onMouseEnter={() => setSlashIdx(i)}
                  className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${i === slashIdx ? 'bg-white/10 text-white' : 'text-white/55 hover:bg-white/5 hover:text-white/80'}`}
                >
                  <span className="text-[10px] font-mono font-bold text-celestial-saturn shrink-0 w-16">/{cmd.id}</span>
                  <span className="text-xs">{cmd.label}</span>
                  <span className="ml-auto text-[10px] text-white/25 whitespace-nowrap">{cmd.desc}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* @Mention Autocomplete */}
      <AnimatePresence>
        {showMentionMenu && filteredMentions.length > 0 && (
          <motion.div
            ref={mentionMenuRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="relative z-50 mb-1"
          >
            <div className="absolute bottom-full left-0 w-56 rounded-xl border border-white/10 bg-zinc-900/95 p-1 shadow-2xl backdrop-blur-xl max-h-48 overflow-y-auto custom-scrollbar">
              {filteredMentions.slice(0, 8).map((item, i) => (
                <button
                  key={item.type + ":" + item.name}
                  type="button"
                  onClick={() => handleMentionSelect(item)}
                  onMouseEnter={() => setMentionIndex(i)}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs transition-colors ${i === mentionIndex ? "bg-white/10 text-white" : "text-white/55 hover:bg-white/5 hover:text-white/80"}`}
                >
                  {item.type === "skill" ? (
                    <Sparkles size={14} className="text-celestial-saturn shrink-0" />
                  ) : (
                    <Globe size={14} className="text-emerald-400 shrink-0" />
                  )}
                  <span className="capitalize">{item.name}</span>
                  <span className="ml-auto text-[10px] text-white/25">{item.type === "skill" ? "技能" : "连接器"}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <form id="chat-input-form" onSubmit={handleSubmit} onPaste={handlePaste}>
        <div className="relative rounded-2xl border border-white/10 bg-black/40 focus-within:border-celestial-saturn/40 focus-within:ring-2 focus-within:ring-celestial-saturn/20 transition-all">
          {/* v3.6.63: 顶部拖拽手柄（双击重置默认高度） */}
          <div
            onMouseDown={onResizeHandleMouseDown}
            onDoubleClick={onResizeHandleDoubleClick}
            title="拖拽调整输入框高度（双击重置）"
            className="absolute top-0 left-0 right-0 h-1.5 flex items-center justify-center cursor-ns-resize group z-10 hover:bg-white/5 rounded-t-2xl"
          >
            <GripHorizontal size={12} className="text-white/20 group-hover:text-white/50 transition-colors" />
          </div>
          <textarea
            ref={messageInputRef}
            value={draftText}
            onChange={(e) => {
              const val = e.target.value;
              onDraftChange(val);
              const slashMatch = val.match(/^\/([a-z]*)/i);
              if (slashMatch) {
                setShowSlashMenu(true);
                setSlashQuery(slashMatch[1] || '');
                setSlashIdx(0);
                setShowMentionMenu(false);
              } else {
                setShowSlashMenu(false);
              }
              setMentionQuery(prev => {
                const m = val.match(/@(skill|connector)?\s?(\w*)$/);
                return m ? (m[2] || '') : prev;
              });
              // v3.6.67: 去掉 auto-grow (用户反映输入文字后高度回缩)
              // 高度由用户拖拽手柄/双击重置/持久化值决定,文本变化不影响高度
              // (textarea 的 content 自动 scroll,不会因为文本多被截断)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (canSend) e.currentTarget.form?.requestSubmit();
              }
            }}
            placeholder={t?.communicatePlaceholder || "与你的本质沟通..."}
            rows={2}
            style={{ height: inputHeight + 'px', paddingTop: 18, paddingBottom: 8 }}
            className="block w-full resize-none bg-transparent px-4 text-sm text-white/90 placeholder:text-white/25 outline-none custom-scrollbar rounded-2xl disabled:opacity-50"
            disabled={isTyping || disabled}
          />
        </div>
      </form>
    </div>
  );
}
